import Warning from "../components/Warning";
import Alert from "../components/Alert";
import RefundConversionNotice from "../components/RefundConversionNotice";
import { useState, useEffect, useRef } from "react";
import PrimaryButton from "../components/PrimaryButton";
import {
  Session,
  makeRequest,
  ResponseNotOkError,
} from "@paywithglide/glide-js";
import {
  Copy,
  CheckCheck,
  CircleCheckBig,
  CircleCheck,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { useGlideDepositConfig } from "../lib/useDepositConfig";
import { formatDisplayWalletAddress } from "../lib/formatDisplayWalletAddress";
import { logger } from "../lib/logger";
import { refundChainOf } from "../lib/refundChain";

type Props = {
  session: Session;
  refundToken?: string;
};

export default function OverpaymentRefund({ session, refundToken }: Props) {
  const { glideConfig, mode, ...depositConfig } = useGlideDepositConfig();
  const requiredAmountUSD = Number(session.paymentAmountUSD || 0);
  const actualAmountUSD = Number(session.actualPaymentAmountUSD || 0);
  const refundAmountUSD = Math.max(0, actualAmountUSD - requiredAmountUSD);
  const [refundAddress, setRefundAddress] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [didCopyPaymentAddress, setDidCopyPaymentAddress] = useState(false);
  const [refundSubmitted, setRefundSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showTransactionDetails, setShowTransactionDetails] = useState(false);
  const refundInputRef = useRef<HTMLInputElement>(null);
  const refundChain = refundChainOf(session);

  const onClick = async () => {
    if (refundAddress.trim()) {
      setIsLoading(true);
      setErrorMessage(null);
      try {
        await makeRequest(glideConfig, {
          url: `/sessions/${session.sessionId}/refund`,
          method: "POST",
          body: {
            refundAddress: refundAddress.trim(),
            refundToken,
          },
        });
        setRefundSubmitted(true);
      } catch (error) {
        logger.error(
          `[OverpaymentRefund] Failed to submit refund request: ${error}`,
        );
        if (error instanceof ResponseNotOkError) {
          const rawBody = error.response;
          try {
            const errorData = JSON.parse(rawBody);
            if (errorData.error) {
              setErrorMessage(errorData.error);
            } else {
              setErrorMessage(
                "Failed to submit refund request. Please try again.",
              );
            }
          } catch {
            setErrorMessage(
              "Failed to submit refund request. Please try again.",
            );
          }
        } else {
          setErrorMessage("Failed to submit refund request. Please try again.");
        }
      } finally {
        setIsLoading(false);
      }
    }
  };

  const onCopyPaymentAddress = () => {
    if (session.depositAddress) {
      navigator.clipboard.writeText(session.depositAddress);
      setDidCopyPaymentAddress(true);
      setTimeout(() => setDidCopyPaymentAddress(false), 3000);
    }
  };

  useEffect(() => {
    // Focus the refund input when component mounts and refund hasn't been submitted
    if (!refundSubmitted && refundInputRef.current) {
      refundInputRef.current.focus();
    }
  }, [refundSubmitted]);

  return (
    <>
      {depositConfig.embed ? null : (
        <div className="mt-6 mb-4 flex flex-col items-center justify-center">
          <img
            src={depositConfig.appLogoUrl}
            className="h-[--app-logo-height] w-auto"
            alt={`${depositConfig.appName} Logo`}
          />
        </div>
      )}

      <div className="mt-6 flex flex-col items-center justify-center">
        <CircleCheck className="h-12 w-12 text-green-600" />
        <div className="font-medium text-lg text-center text-[--color-text-primary]">
          Success
        </div>
        <div className="text-[--color-text-secondary] text-center text-sm">
          Your transaction was completed successfully.
          <br />
          {/* The excess payment will be refunded to your address. */}
        </div>
      </div>

      <Warning className="my-4">
        <div className="font-medium mb-1">Excess Payment Amount</div>
        <div>
          You paid ${actualAmountUSD.toFixed(2)} instead of $
          {requiredAmountUSD.toFixed(2)}. Enter your refund address below to get
          a refund of ${refundAmountUSD.toFixed(2)}.
        </div>
      </Warning>

      {refundSubmitted ? (
        <Alert
          icon={<CircleCheckBig strokeWidth={2.25} className="w-4 h-4" />}
          title="Refund Request Submitted"
          subtitle="Your refund request has been submitted successfully. You should receive your excess payment refund within the next few hours."
          className="mb-6"
        />
      ) : (
        <>
          <RefundConversionNotice session={session} className="mb-4" />

          <div className="mb-3">
            <div className="mb-1 font-medium text-sm text-[--color-text-primary]">
              Refund Address
            </div>
            <div className="text-xs text-[--color-text-secondary] mb-3">
              Enter your {refundChain.name} wallet address below to receive your
              excess payment refund.
            </div>

            <div className="flex items-center gap-1 bg-[--color-background-secondary] p-2 rounded-md">
              {refundChain.logoUrl && (
                <img
                  src={refundChain.logoUrl}
                  alt={refundChain.name}
                  className="h-4 w-4 rounded-full flex-shrink-0"
                />
              )}
              <input
                ref={refundInputRef}
                className="flex-1 bg-transparent text-[--color-text-primary] text-sm outline-none placeholder:text-[--color-text-secondary]"
                value={refundAddress}
                onChange={(e) => setRefundAddress(e.target.value)}
                placeholder={refundChain.placeholder}
              />
            </div>
          </div>

          <PrimaryButton
            onClick={() => onClick()}
            loading={isLoading}
            disabled={!refundAddress.trim()}
            className="mt-4"
          >
            {refundAddress.trim() ? "Request Refund" : "Enter refund address"}
          </PrimaryButton>

          {errorMessage && (
            <Warning className="mt-4">
              <div className="font-medium mb-1">Error</div>
              <div>{errorMessage}</div>
            </Warning>
          )}
        </>
      )}

      <div className="mt-4">
        <button
          onClick={() => setShowTransactionDetails(!showTransactionDetails)}
          className="flex items-center gap-0.5 text-[--color-text-secondary] hover:text-[--color-text-primary] transition-colors text-xs h-7"
        >
          {showTransactionDetails ? (
            <>
              <ChevronUp className="h-4 w-4" /> Hide transaction details
            </>
          ) : (
            <>
              <ChevronDown className="h-4 w-4" /> Show transaction details
            </>
          )}
        </button>

        {showTransactionDetails ? (
          <div className="mt-1">
            {session.paymentTransactionHash && session.paymentTransactionUrl ? (
              <div className="flex flex-col gap-2 sm:gap-0 sm:flex-row sm:items-center justify-between h-7">
                <div className="text-xs text-[--color-text-secondary]">
                  {mode === "deposit" ? (
                    <>Transfer Transaction</>
                  ) : mode === "withdraw" ? (
                    <>Withdrawal Transaction</>
                  ) : mode === "pay" ||
                    mode === "call" ||
                    mode === "buy" ||
                    mode === "refund" ? (
                    <>Payment Transaction</>
                  ) : null}
                </div>
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href={session.paymentTransactionUrl}
                  className="flex items-center gap-1"
                >
                  <img
                    alt={session.actualPaymentChainName}
                    src={session.actualPaymentChainLogoUrl}
                    className="h-4 w-4 rounded-full"
                  />
                  <span className="text-xs text-blue-500 font-mono hover:underline">
                    {formatDisplayWalletAddress(session.paymentTransactionHash)}
                  </span>
                </a>
              </div>
            ) : null}

            {mode === "deposit" ||
            mode === "withdraw" ||
            mode === "call" ||
            mode === "buy" ? (
              <div className="flex flex-col gap-2 sm:gap-0 sm:flex-row sm:items-center items-center justify-between h-7">
                <div className="text-xs text-[--color-text-secondary]">
                  {mode === "deposit" || mode === "buy" ? (
                    <>Deposit Transaction</>
                  ) : mode === "withdraw" ? (
                    <>Transfer Transaction</>
                  ) : mode === "call" ? (
                    <>Fulfillment Transaction</>
                  ) : null}
                </div>
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href={session.sponsoredTransactionUrl!}
                  className="flex items-center gap-1"
                >
                  <img
                    alt={session.sponsoredTransactionChainName}
                    src={session.sponsoredTransactionChainLogoUrl}
                    className="h-4 w-4 rounded-full"
                  />
                  <span className="text-xs text-blue-500 hover:underline">
                    {formatDisplayWalletAddress(
                      session.sponsoredTransactionHash || "0x0",
                    )}
                  </span>
                </a>
              </div>
            ) : null}

            <div className="flex justify-between items-center h-7">
              <div className="text-xs text-[--color-text-secondary]">
                Payment Chain
              </div>
              <div className="flex items-center gap-1">
                {session.actualPaymentChainLogoUrl && (
                  <img
                    src={session.actualPaymentChainLogoUrl}
                    alt={session.actualPaymentChainName}
                    className="h-4 w-4 rounded-full flex-shrink-0"
                  />
                )}
                <span className="text-xs text-[--color-text-primary]">
                  {session.actualPaymentChainName}
                </span>
              </div>
            </div>

            <div className="flex justify-between items-center h-7">
              <div className="text-xs text-[--color-text-secondary]">
                Payment Address
              </div>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  className="p-1 hover:bg-[--color-background-secondary] rounded transition-colors"
                  onClick={onCopyPaymentAddress}
                  aria-label="Copy payment address"
                >
                  {didCopyPaymentAddress ? (
                    <CheckCheck
                      className="w-4 h-4 text-green-500"
                      strokeWidth={2}
                    />
                  ) : (
                    <Copy
                      className="w-4 h-4 text-[--color-text-secondary]"
                      strokeWidth={2}
                    />
                  )}
                </button>
                <div className="text-xs text-[--color-text-primary]">
                  {session.depositAddress && session.depositAddress.length > 20
                    ? `${session.depositAddress.slice(0, 8)}...${session.depositAddress.slice(-8)}`
                    : session.depositAddress}
                </div>
              </div>
            </div>

            <div className="flex justify-between items-center h-7">
              <div className="text-xs text-[--color-text-secondary]">
                Expected Amount
              </div>
              <div className="text-xs text-[--color-text-primary]">
                {session.paymentAmount} {session.paymentCurrencySymbol} ($
                {Number(session.paymentAmountUSD).toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
                )
              </div>
            </div>

            <div className="flex justify-between items-center h-7">
              <div className="text-xs text-[--color-text-secondary]">
                Your Payment
              </div>
              <div className="text-xs text-[--color-text-primary]">
                {session.actualPaymentAmount}{" "}
                {session.actualPaymentCurrencySymbol} ($
                {Number(session.actualPaymentAmountUSD).toLocaleString(
                  undefined,
                  {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  },
                )}
                )
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </>
  );
}
